/**
 * Aggregates unique permissions from all roles where accessLevel > 0 (includes viewOnly and fullAccess)
 * @param roles - Array of roles from currentUser
 * @returns Set of unique permission keys
 */
export const getAggregatedPermissions = (roles: any[]): Set<string> => {
  const permissionKeys = new Set<string>();

  if (!roles || roles.length === 0) {
    return permissionKeys;
  }

  roles.forEach((role) => {
    if (role.permissions && Array.isArray(role.permissions)) {
      role.permissions.forEach((permission: any) => {
        if (permission.accessLevel > 0 && permission.key) {
          permissionKeys.add(permission.key);
        }
      });
    }
  });

  return permissionKeys;
};

/**
 * Permission keys mapping to sidebar menu items
 */
export const PermissionKeys = {
  Orders: "Orders",
  Clients: "Clients",
  Users: "Users",
  Roles: "Roles",
  InspectorFirms: "InspectorFirms",
  Invoices: "Invoices",
  FormTemplates: "FormTemplates",
  Inspections: "Inspections",
  Forms: "Forms",
};

/**
 * Checks if user has a specific permission
 * @param permissions - Set of permission keys
 * @param permissionKey - The permission key to check
 * @returns boolean
 */
export const hasPermission = (
  permissions: Set<string>,
  permissionKey: string
): boolean => {
  return permissions.has(permissionKey);
};

/**
 * Gets the maximum access level for a specific permission key across all roles
 * @param roles - Array of roles from currentUser
 * @param permissionKey - The permission key to check
 * @returns The maximum access level (0 = noAccess, 1 = readOnly, >1 = fullAccess)
 */
export const getPermissionAccessLevel = (
  roles: any[],
  permissionKey: string
): number => {
  if (!roles || roles.length === 0) {
    return 0;
  }

  let maxAccessLevel = 0;

  roles.forEach((role) => {
    if (role.permissions && Array.isArray(role.permissions)) {
      role.permissions.forEach((permission: any) => {
        if (permission.key === permissionKey && permission.accessLevel > maxAccessLevel) {
          maxAccessLevel = permission.accessLevel;
        }
      });
    }
  });

  return maxAccessLevel;
};

/**
 * Checks if user has read-only access for a specific permission
 * @param roles - Array of roles from currentUser
 * @param permissionKey - The permission key to check
 * @returns boolean - true if user has read-only access (accessLevel === 1)
 */
export const hasReadOnlyAccess = (
  roles: any[],
  permissionKey: string
): boolean => {
  const accessLevel = getPermissionAccessLevel(roles, permissionKey);
  return accessLevel === 1;
};

/**
 * Checks if user has full/write access for a specific permission
 * @param roles - Array of roles from currentUser
 * @param permissionKey - The permission key to check
 * @returns boolean - true if user has full access (accessLevel > 1)
 */
export const hasFullAccess = (
  roles: any[],
  permissionKey: string
): boolean => {
  const accessLevel = getPermissionAccessLevel(roles, permissionKey);
  return accessLevel > 1;
};
